import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, ListGroup, ListRow, Skeleton, Switch } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { CopyText } from '../servers-ui.tsx';
import {
  type ClientPlatform,
  guessClientPlatform,
  QrCode,
  type VpnApp,
  VpnAppGuide,
  VpnAppsList,
} from '../vpn.tsx';

export function VpnSubscriptionScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t, money, dateTime } = usePrefs();
  const [guideApp, setGuideApp] = useState<VpnApp | null>(null);
  const [platform, setPlatform] = useState<ClientPlatform>(guessClientPlatform);
  const config = useQuery(trpc.config.queryOptions());
  const subscription = useQuery({
    ...trpc.vpnSubscription.get.queryOptions(),
    refetchInterval: (query) => (query.state.data?.status === 'provisioning' ? 3_000 : false),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries(trpc.vpnSubscription.get.pathFilter());
    await queryClient.invalidateQueries(trpc.balance.summary.pathFilter());
  };
  const purchase = useMutation(trpc.vpnSubscription.purchase.mutationOptions({ onSuccess: refresh }));
  const autoRenew = useMutation(trpc.vpnSubscription.setAutoRenew.mutationOptions({ onSuccess: refresh }));
  const data = subscription.data;
  const price = config.data?.sharedVpn.priceMonthly ?? data?.priceMonthly ?? 0;

  if (guideApp?.id && data?.key) {
    return <VpnAppGuide app={guideApp} vpnKey={data.key} onBack={() => setGuideApp(null)} />;
  }
  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">{t.sharedVpn.title}</h1>
      {subscription.isPending || config.isPending ? (
        <Skeleton height={240} />
      ) : data?.status === 'active' && data.key ? (
        <div className="ul-stack">
          <div className="ul-vpn-key">
            <QrCode text={data.key} label={t.vpn.qr} />
            <div className="ul-vpn-key__body">
              <span className="ul-vpn-key__title">
                <Badge tone="ok">{t.vpn.ready}</Badge>
              </span>
              <span className="ul-vpn-key__text">{t.vpn.scanLink}</span>
              <CopyText text={data.key} />
            </div>
          </div>
          <ListGroup header={t.sharedVpn.key} footer={t.sharedVpn.validUntil(dateTime(data.paidUntil))}>
            <ListRow
              icon="key"
              title={<span className="ul-mono ul-wrap ul-clamp">{data.key}</span>}
              trailing={<CopyText text={data.key} />}
            />
            <ListRow
              icon="clock"
              title={t.sharedVpn.autoRenew}
              subtitle={t.sharedVpn.price(money(data.priceMonthly))}
              trailing={
                <Switch
                  label={t.sharedVpn.autoRenew}
                  checked={data.autoRenew}
                  onChange={(checked) => autoRenew.mutate({ autoRenew: checked })}
                />
              }
            />
          </ListGroup>
          <VpnAppsList
            kind="vless"
            platform={platform}
            onPlatformChange={setPlatform}
            onSelect={setGuideApp}
          />
        </div>
      ) : data?.status === 'provisioning' ? (
        <ListGroup>
          <ListRow icon="clock" title={t.sharedVpn.preparing} subtitle={t.sharedVpn.preparingText} />
        </ListGroup>
      ) : (
        <div className="ul-stack">
          <ListGroup header={t.sharedVpn.intro}>
            <ListRow icon="user" title={t.sharedVpn.devices} />
            <ListRow icon="chart" title={t.sharedVpn.traffic} />
            <ListRow icon="clock" title={t.sharedVpn.renewal} />
            <ListRow
              icon="shield"
              title={t.sharedVpn.ownTitle}
              subtitle={t.sharedVpn.ownText}
              chevron
              onClick={() => nav.push({ name: 'serverCreate', vpn: true })}
            />
          </ListGroup>
          {purchase.isError ? (
            <div className="ul-stack">
              <div className="ul-error" role="alert">
                {errorText(purchase.error)}
              </div>
              <Button block onClick={() => nav.push({ name: 'topup', amountCents: price })}>
                {t.common.topUpBy(money(price))}
              </Button>
            </div>
          ) : null}
          <Button
            size="l"
            block
            icon="key"
            loading={purchase.isPending}
            disabled={!config.data?.sharedVpn.available}
            onClick={() => purchase.mutate()}
          >
            {t.sharedVpn.buy(money(price))}
          </Button>
        </div>
      )}
    </main>
  );
}
